import { LineChart, Line, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Dot } from 'lucide-react';

type ParameterCardProps = {
  label: string;
  unit?: string;
  value: string | null;
  description: string;
  onClick: () => void;
  chartData?: Array<{ time: string; value: number }>;
  children?: React.ReactNode;
}
type Status = 'normal' | 'warning' | 'danger';

const nonVolatile = ['LONGITUDE', 'LATITUDE'];

const statusStyles = {
  normal: {
    text: 'text-teal-500',
    bg: 'bg-teal-500'
  },
  warning: {
    text: 'text-yellow-600',
    bg: 'bg-yellow-600'    
  },
  danger: {
    text: 'text-red-600',
    bg: 'bg-red-600'    
  }
}
export default function ParameterCard({ label, unit, value, onClick, chartData, children }: ParameterCardProps) {
  // Filter out any NaN values from chart data


  const validChartData = chartData ? 
    chartData.filter(point => !isNaN(point.value)) : 
    [];
  
  // If value is null or undefined, display N/A
  const displayValue = value ? value : 'N/A';
  
  function getPseudoStatus(value: string | null) {
    if (!value) return 'normal';

    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash += value.charCodeAt(i);
    }

    const statuses = ['normal', 'warning', 'danger'];
    return statuses[(hash+22) % statuses.length] as Status;
  }

  const status = getPseudoStatus(label);

  return (
    <div
      className="bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
    <div className="flex items-center gap-2">
      <p className="text-sm text-sensor font-semibold tracking-wider">
        {label}
      </p>

      {unit && (
        <p className="text-[10px] ml-auto">
          {unit}
        </p>
      )}
    </div>
      <span className='flex gap-2 items-center'>
        <p className="text-xl font-bold text-gray-800 mt-1">{displayValue}</p>
        {!nonVolatile.includes(label) &&
        <span className="relative flex h-2 w-2">
          <span className={`animate-ping [animation-duration:2.5s] absolute inline-flex h-full w-full rounded-full opacity-70 ${statusStyles[status].bg}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${statusStyles[status].bg}`} />
        </span>
          }
      </span>
      {validChartData.length > 0 && !children && (
        <div className="h-12 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={validChartData}>
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#005E6E" 
                strokeWidth={1.5} 
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {children}
    </div>
  );
} 